import { cn } from "@/lib/styles.utils";
import {
  useContext,
  useId,
  type FC,
  type ReactNode,
  createContext,
} from "react";

interface ModalProps {
  children: ReactNode;
}

type Ctx = { labelId: string; descId: string };

const ModalCtx = createContext<Ctx | null>(null);

const useModalCtx = () => {
  const ctx = useContext(ModalCtx);
  if (!ctx)
    throw new Error(
      "ModalMobile subcomponent must be used within <ModalMobile>",
    );
  return ctx;
};

const ModalMobile: FC<ModalProps> & {
  Header: FC<{ children: ReactNode; className?: string }>;
  Content: FC<{ children: ReactNode; className?: string }>;
  Footer: FC<{ children: ReactNode; className?: string }>;
} = ({ children }) => {
  const labelId = useId();
  const descId = useId();

  return (
    <ModalCtx.Provider value={{ labelId, descId }}>
      <div
        data-testid="modal-mobile"
        className="fixed overflow-hidden left-0 right-0 bg-ib-black bg-opacity-70 w-full top-0 bottom-0 z-50 flex items-end "
        aria-modal="true"
        role="dialog"
        aria-labelledby={labelId}
        aria-describedby={descId}
      >
        <div
          className={
            "w-full max-h-[90vh] bg-white rounded-t-2xl shadow-lg flex flex-col animate-[bottomsheet-up_0.25s_ease-out_forwards]"
          }
        >
          {children}
        </div>
      </div>
    </ModalCtx.Provider>
  );
};

const ModalHeader: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const { labelId } = useModalCtx();
  return (
    <div
      data-testid="answer-modal-header"
      id={labelId}
      className={cn("px-3 py-2", className || "")}
    >
      {children}
    </div>
  );
};

const ModalContent: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const { descId } = useModalCtx();
  return (
    <div
      data-testid="answer-modal-content"
      id={descId}
      className={cn("px-3 py-2", className || "")}
    >
      {children}
    </div>
  );
};

const ModalFooter: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div
    data-testid="answer-modal-footer"
    className={cn(
      "px-6 py-4 border-t border-gray-200 flex justify-end gap-3",
      className || "",
    )}
  >
    {children}
  </div>
);

ModalMobile.Header = ModalHeader;
ModalMobile.Content = ModalContent;
ModalMobile.Footer = ModalFooter;

export { ModalMobile };
